import type { I18n, MessageDescriptor } from '@lingui/core';

import type { ApiErrorMessage } from './APIError';
import APIError from './APIError';

/**
 * Source-locale copy for a descriptor with no `i18n` to resolve against. It duplicates
 * `DEFAULT_ERROR_MESSAGE` rather than reading it, because that constant is itself a
 * descriptor whose text the production macro strips.
 */
export const UNRESOLVED_DESCRIPTOR_COPY =
  'We were unable to process your request. Please try again shortly or contact Customer Support.';

/**
 * Resolves a descriptor against the request's `i18n`. Not every store is configured with
 * one — `createStore`'s default extra argument is empty, and `Provider` reads an `i18n`
 * that is absent in trees with no `I18nProvider` — so fall back to copy a user can act on.
 * The descriptor's own id is never it: this text is rendered, and in production the id is
 * a content hash. Never fall back to the `@lingui/core` global either: on the server that
 * instance belongs to whichever request activated it last.
 */
const resolveDescriptor = (descriptor: MessageDescriptor, i18n: I18n | undefined): string => {
  if (i18n) {
    return i18n._(descriptor);
  }

  return descriptor.message ?? UNRESOLVED_DESCRIPTOR_COPY;
};

/**
 * Resolves one of the mixed authored/server-supplied messages that `parseApiErrors`
 * produces. Strings came from the API and are passed through as-is.
 */
export const resolveApiErrorMessage = (message: ApiErrorMessage, i18n?: I18n): string =>
  typeof message === 'string' ? message : resolveDescriptor(message, i18n);

/**
 * Resolves the user-facing text of a thrown error. Anything carrying a `message` falls
 * back to it — not only real `Error`s, since some code paths reject with a bare object.
 */
export const resolveErrorMessage = (error: unknown, i18n?: I18n): string => {
  if (error instanceof APIError && error.descriptor) {
    return resolveDescriptor(error.descriptor, i18n);
  }

  const message = (error as { message?: unknown } | null)?.message;

  return typeof message === 'string' ? message : String(error);
};

export default resolveApiErrorMessage;
